import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import nextStep, { initialMachineState } from '../core/machine';
import { SpeedyFlowEvent, SpeedyFlowOpenPayload, SpeedyFlowResolution, SpeedyFlowState } from '../types';

const INITIAL_STATE: SpeedyFlowState = { visible: false };

const speedyFlowSlice = createSlice({
  name: 'speedyFlow',
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Open the flow on an already-resolved decision. The resolution is computed by
     * `resolveSpeedyFlow` at the trigger site, so this reducer never decides anything itself.
     *
     * `ppt` and `offerId` are snapshotted here on purpose: the products prompt strips them from the
     * URL on dismiss and `/start` deletes them outright, so a flow that re-read the URL mid-journey
     * would lose the visitor's offer somewhere around the social-login round trip (ADR 0004).
     */
    openSpeedyFlow: (_state, { payload }: PayloadAction<SpeedyFlowOpenPayload>) => {
      const { resolution, gate, utm, ppt, offerId, hasAccount, hasPersona, email, productChoice, openedAtPath } = payload;
      if (resolution.flow === 'none') {
        return INITIAL_STATE;
      }
      const { flow, initialStep, completionVariant, ...products } = resolution;
      return {
        visible: true,
        gate,
        utm,
        ppt,
        offerId,
        email,
        openedAtPath,
        resumedChoice: payload.resumedChoice,
        ...products,
        machine: initialMachineState({ flow, initialStep, hasAccount, hasPersona, completionVariant, productChoice }),
      };
    },

    /**
     * Feed the machine an event. The modal closes when the machine reaches its terminal.
     *
     * A machine that did not move returns the state it was given, and that is passed straight
     * through rather than wrapped in a new object. `factsChanged` fires on every user refetch —
     * most of which report nothing new — and re-rendering the whole modal for each would be a
     * needless cost at best, and at worst would interrupt a form the visitor is typing into.
     */
    advanceSpeedyFlow: (state, { payload }: PayloadAction<SpeedyFlowEvent>) => {
      if (!state.machine) {
        return state;
      }
      const machine = nextStep(state.machine, payload);
      if (machine === state.machine) {
        return state;
      }
      return { ...state, machine, visible: machine.step !== 'closed' };
    },

    /** Remember the address between the sign-up form and the verification step. */
    setSpeedyFlowEmail: (state, { payload }: PayloadAction<string>) => ({ ...state, email: payload }),

    /**
     * The app navigated. Close the flow if it left the page the flow belongs to.
     *
     * Fed by the router rather than by `popstate`, because `route()` uses `pushState`, which emits
     * no event — so a programmatic redirect (an expired session bouncing to `/login`, say) used to
     * leave the modal floating over a page it had nothing to do with.
     *
     * Compares the path only. Query strings change underneath a running flow — `ppt` gets stripped,
     * checkout markers get added — and none of that means the visitor went anywhere.
     */
    speedyFlowNavigated: (state, { payload }: PayloadAction<string>) => {
      if (!state.visible || !state.openedAtPath || state.openedAtPath === payload) {
        return state;
      }
      return INITIAL_STATE;
    },

    closeSpeedyFlow: () => INITIAL_STATE,
  },
});

export type { SpeedyFlowResolution };
export default speedyFlowSlice;
